import type { NotificationItemComponent } from "./NotificationItem";

import React from "react";
import { useNavigate } from "@tanstack/react-router";
import { Typography, Button, ButtonProps } from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import useRecommended from "@/components/subscription/customize/hooks/useRecommended";
import NotificationItem from "./NotificationItem";

import useIntercom from "@/hooks/useIntercom";
import usePlan from "@/hooks/usePlan";
import useCustomizeQuery from "@/components/newcustomize/hooks/useCustomizeQuery";

const HighConcurrencyErrorNotification: NotificationItemComponent = ({
  item,
}) => {
  const navigate = useNavigate();
  const { bandwidth } = useRecommended({ planId: item.context?.plan });
  const { data: plan } = usePlan(item.context?.plan || "default");
  const { link: customizeHref } = useCustomizeQuery({
    source: "notification",
    existingPlanId: item.context?.plan,
    showCustomizeSections: ["additional-features"],
    overrides: {
      isHighConcurrency: true,
    },
  });

  const handleUpgrade = React.useCallback(() => {
    if (!bandwidth) return;
    void navigate({ ...customizeHref, replace: true });
  }, [navigate, bandwidth, customizeHref]);

  const { showNewMessage } = useIntercom();

  const handleContactSupport = () => {
    showNewMessage(
      "I have received high concurrency while on high concurrency plan."
    );
  };

  const buttonProps: ButtonProps = {
    size: "small",
    disableElevation: true,
    color: !item.dismissed_at ? "warning" : "primary",
  };

  return (
    <NotificationItem variant={!item.dismissed_at ? "warning" : "default"}>
      <NotificationItem.Title created_at={item.created_at}>
        <span>High Concurrency Error Detected</span>
      </NotificationItem.Title>
      <NotificationItem.Content>
        <Typography variant="body2">
          More than 3% of your proxy requests are experiencing concurrency
          errors. It may be causing unstable proxy activity. Add High
          Concurrency feature to avoid any interruptions in proxy activity.
        </Typography>
      </NotificationItem.Content>
      <NotificationItem.Actions {...item}>
        {plan?.is_high_concurrency ? (
          <Button
            variant="outlined"
            onClick={handleContactSupport}
            {...buttonProps}
            data-testid="contact-support-button"
          >
            <span>Contact Support</span>
          </Button>
        ) : (
          <Button
            variant={!item.dismissed_at ? "contained" : "outlined"}
            endIcon={<OpenInNewIcon fontSize="small" />}
            onClick={handleUpgrade}
            {...buttonProps}
            data-testid="upgrade-button"
          >
            <span>Upgrade</span>
          </Button>
        )}
      </NotificationItem.Actions>
    </NotificationItem>
  );
};

export default HighConcurrencyErrorNotification;
